"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Высота элемента «ровно до низа видимой области» — без пустоты снизу и без
 * вылезания за край экрана. Для таблиц-«полотен», занимающих вкладку целиком.
 *
 * Зачем не CSS-константа: общий класс `.tbl-fill` режет высоту на фиксированный
 * запас (`100dvh − 220px`), и на вкладке с компактным тулбаром снизу остаётся
 * мёртвая полоса, а с высоким — таблица вылезает за экран. Здесь запас не
 * угадывается, а считается из реального положения элемента.
 *
 * ⚠️ Две грабли, обе уже стоили регрессий:
 *  1. Меряем смещение ВНУТРИ ближайшего скролл-родителя, а не
 *     `getBoundingClientRect().top` — тот зависит от прокрутки, и высота начинает
 *     «плыть» при листании (документ растёт бесконечно, шапка уезжает).
 *  2. Ref — CALLBACK, а не объект: вкладка сначала рендерит «Загрузка…», и на
 *     момент useLayoutEffect нужного узла ещё нет. Callback-ref срабатывает
 *     ровно тогда, когда контейнер реально появился в DOM.
 */
export function useFillHeight<T extends HTMLElement = HTMLDivElement>(reserve = 12, min = 280) {
  const nodeRef = useRef<T | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const measure = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    let sp: HTMLElement | null = el.parentElement;
    for (; sp; sp = sp.parentElement) {
      if (/(auto|scroll|overlay)/.test(getComputedStyle(sp).overflowY)) break;
    }
    const doc = (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
    const box = sp ?? doc;
    const boxTop = box === doc ? 0 : box.getBoundingClientRect().top;
    const view = box === doc ? window.innerHeight : box.clientHeight;
    const offsetInside = el.getBoundingClientRect().top - boxTop + box.scrollTop;
    setMaxHeight(Math.max(min, Math.round(view - offsetInside - reserve)));
  }, [reserve, min]);

  const ref = useCallback((node: T | null) => {
    nodeRef.current = node;
    if (node) {
      measure();
      // Данные догружаются и двигают вёрстку — добираем на следующем кадре.
      requestAnimationFrame(measure);
    }
  }, [measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return { ref, maxHeight, remeasure: measure };
}
